<template>
  <section class="testimonial-stories">
    <div class="container">
      <div class="testimonial-stories__header wow bounceInUp" data-wow-delay="0.1s">
        <h2 class="testimonial-stories__title">{{ $t("testimonialSectionTitle") }}</h2>
        <p class="testimonial-stories__description">
          {{ $t("testimonialSectionDescription") }}
        </p>
      </div>

      <div class="testimonial-stories__scroller wow bounceInUp" data-wow-delay="0.1s">
        <article
          v-for="(person, index) in persons"
          :key="person.id"
          class="story-card"
          :class="{ 'is-expanded': expandedTestimonials[person.id] }"
          :data-wow-delay="`${0.1 + index * 0.05}s`"
        >
            <div class="story-card__stars" aria-hidden="true">
              <i class="fas fa-star" v-for="star in 5" :key="star"></i>
            </div>

            <button
              type="button"
              class="story-card__quote-button"
              @click="toggleExpanded(person.id)"
              :aria-expanded="expandedTestimonials[person.id] ? 'true' : 'false'"
            >
              <span class="story-card__quote">
              "{{ person.description }}"
              </span>
            </button>

            <div class="story-card__person">
              <img
                :src="getImageUrl(person.person_image_url)"
                :alt="person.name"
                class="story-card__avatar"
                loading="lazy"
                decoding="async"
              />
              <div>
                <h3 class="story-card__name">{{ person.name }}</h3>
                <p class="story-card__role">{{ $t("studentRole") }}</p>
              </div>
            </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import mediaUrl from "../utils/mediaUrl";

export default {
  data() {
    return {
      expandedTestimonials: {},
    };
  },
  computed: {
    ...mapGetters(["persons"]),
  },
  methods: {
    ...mapActions(["getStudent"]),
    getImageUrl(imageUrl) {
      return mediaUrl(imageUrl);
    },
    toggleExpanded(id) {
      this.$set(this.expandedTestimonials, id, !this.expandedTestimonials[id]);
    },
  },
  mounted() {
    this.getStudent();
  },
};
</script>

<style scoped>
.testimonial-stories {
  padding: 88px 0;
  background: #310049;
}

.testimonial-stories__header {
  max-width: 760px;
  margin-bottom: 42px;
}

.testimonial-stories__title {
  margin-bottom: 16px;
  font-size: clamp(2rem, 4vw, 3.4rem);
  font-weight: 800;
  line-height: 1.08;
  color: #ffffff;
}

.testimonial-stories__description {
  margin: 0;
  font-size: 1.12rem;
  line-height: 1.7;
  color: rgba(235, 226, 247, 0.78);
}

.testimonial-stories__scroller {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(320px, 380px);
  gap: 24px;
  overflow-x: auto;
  padding-bottom: 12px;
  scroll-snap-type: x proximity;
  scrollbar-width: thin;
  scrollbar-color: rgba(159, 238, 255, 0.4) transparent;
}

.testimonial-stories__scroller::-webkit-scrollbar {
  height: 10px;
}

.testimonial-stories__scroller::-webkit-scrollbar-thumb {
  background: rgba(159, 238, 255, 0.35);
  border-radius: 999px;
}

.story-card {
  display: flex;
  flex-direction: column;
  scroll-snap-align: start;
  min-height: 360px;
  padding: 30px 30px 28px;
  border-radius: 24px;
  background:
    linear-gradient(180deg, rgba(117, 53, 164, 0.88) 0%, rgba(92, 28, 135, 0.96) 100%);
  border: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: 0 18px 40px rgba(17, 4, 35, 0.22);
}

.story-card__stars {
  display: flex;
  gap: 8px;
  margin-bottom: 22px;
  color: #9feeff;
  font-size: 1.15rem;
}

.story-card__quote-button {
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.story-card__quote {
  position: relative;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
  overflow: hidden;
  min-height: 140px;
  max-height: 140px;
  margin-bottom: 28px;
  font-size: 1.02rem;
  line-height: 1.75;
  font-style: italic;
  color: #f1e7fa;
}

.story-card__quote-button:not(:focus-visible) {
  outline: none;
}

.story-card__quote-button:focus-visible .story-card__quote {
  outline: 2px solid rgba(159, 238, 255, 0.9);
  outline-offset: 6px;
  border-radius: 12px;
}

.story-card__person {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: auto;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.story-card__avatar {
  width: 58px;
  height: 58px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid rgba(159, 238, 255, 0.2);
}

.story-card__name {
  margin: 0 0 4px;
  font-size: 1.45rem;
  font-weight: 800;
  color: #ffffff;
}

.story-card__role {
  margin: 0;
  font-size: 1rem;
  color: #8ed6f1;
}

.story-card.is-expanded .story-card__quote {
  display: block;
  -webkit-line-clamp: unset;
  max-height: none;
}

@media (max-width: 991px) {
  .testimonial-stories {
    padding: 72px 0;
  }

  .testimonial-stories__scroller {
    grid-auto-columns: minmax(300px, 340px);
  }

  .story-card {
    min-height: 330px;
  }
}

@media (max-width: 576px) {
  .testimonial-stories {
    padding: 56px 0;
  }

  .testimonial-stories__header {
    margin-bottom: 28px;
  }

  .testimonial-stories__description {
    font-size: 1rem;
  }

  .testimonial-stories__scroller {
    grid-auto-columns: 84vw;
    gap: 18px;
  }

  .story-card {
    min-height: auto;
    padding: 24px 20px 22px;
  }

  .story-card__quote {
    min-height: 132px;
    max-height: 132px;
    -webkit-line-clamp: 4;
    margin-bottom: 24px;
    font-size: 1rem;
  }

  .story-card__name {
    font-size: 1.35rem;
  }
}
</style>
